import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import { getLastSearchedVehicleKey } from 'domains/catalog/Catalog.store';
import { useFetchEasyBasketOperations } from 'domains/easyBasket/EasyBasket.requests';
import {
  fetchEasyBasketOperationDetailsRequestSaga,
  getAllEasyBasketOperationIds,
  getEasyBasketOperationDetails,
  getEasyBasketOperations,
  getEasyBasketSelectedOperationId,
  resetLaborTimeSelection,
  resetReferenceSelection,
  setSelectedOperationId,
} from 'domains/easyBasket/EasyBasket.store';
import {
  EasyBasketTabs,
  TAB_LABOR_RATE_SELECTION,
  TAB_PARTS_SELECTION,
  TAB_SELECT_OPERATION,
} from 'domains/easyBasket/EasyBasket.types';
import { addEasyBasketOperationToEstimate } from 'domains/estimate/Estimate.store';
import LaborTimesSelection from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/LaborTimesSelection/LaborTimesSelection';
import LaborTimesTotalStrip from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/LaborTimesSelection/LaborTimesTotalStrip';
import SelectOperation from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/OperationSelection';
import PartsSelection from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/PartsSelection';
import ReferencesTotalStrip from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/PartsSelection/ReferencesTotalStrip';
import { WideTabs } from 'UI';
import { hasData } from 'utils';
import { OFFSET_PADDING, OFFSET_SUB_HEADER, useOffsetTop } from 'utils/hooks/useOffsetTop';
import { scrollToTopSmooth } from 'utils/hooks/useResetScroll';

export interface OperationsSectionProps {
  operationsIds: string[];
  categoryLabel: string;
  plateId?: string;
}

const OperationsSection = ({ operationsIds, categoryLabel, plateId }: Readonly<OperationsSectionProps>) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const offsetTop = useOffsetTop('RENAULT_CATALOG');
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const [activeTabKey, setActiveTabKey] = useState<EasyBasketTabs>(TAB_SELECT_OPERATION);
  const allOperationsIds = useSelector(getAllEasyBasketOperationIds);
  const operations = useSelector(getEasyBasketOperations);
  const selectedOperationId = useSelector((state: RootState) => getEasyBasketSelectedOperationId(state));
  const selectedOperationDetails = useSelector((state: RootState) =>
    getEasyBasketOperationDetails(state, selectedOperationId ?? ''),
  );
  const selectedOperationSearchStatus = selectedOperationDetails?.searchStatus;

  useFetchEasyBasketOperations(operationsIds);

  const handleSelectOperation = (operationId: string) => {
    if (!vehicleKey) {
      return;
    }
    const isOperationDataLoaded = allOperationsIds.includes(operationId);

    dispatch(setSelectedOperationId({ operationId }));
    dispatch(resetReferenceSelection());
    dispatch(resetLaborTimeSelection());

    if (isOperationDataLoaded) {
      setActiveTabKey(TAB_PARTS_SELECTION);
      return;
    }

    const genericParts = operations.get(operationId)?.data?.genericParts ?? [];

    dispatch(
      fetchEasyBasketOperationDetailsRequestSaga({
        vehicleKey,
        genericParts,
        operationsIds: [operationId],
      }),
    );
  };

  const handleClickOnNext = () => {
    if (activeTabKey === TAB_PARTS_SELECTION) {
      setActiveTabKey(TAB_LABOR_RATE_SELECTION);
    }
  };

  useEffect(() => {
    if (selectedOperationId && hasData(selectedOperationDetails?.searchStatus)) {
      setActiveTabKey(TAB_PARTS_SELECTION);
    }
  }, [selectedOperationId, selectedOperationDetails]);

  useEffect(() => {
    setActiveTabKey(TAB_SELECT_OPERATION);
  }, [plateId]);

  const handleAddOperationToEstimate = () => {
    dispatch(addEasyBasketOperationToEstimate());
  };

  const tabData = [
    {
      title: t('catalog.easy_basket.tab.select_operation', 'Select operation'),
      key: TAB_SELECT_OPERATION,
      component: (
        <SelectOperation
          categoryLabel={categoryLabel}
          operationsIds={operationsIds}
          onSelectOperation={handleSelectOperation}
          selectedOperationSearchStatus={selectedOperationSearchStatus}
        />
      ),
    },
    {
      title: t('catalog.easy_basket.tab.parts_selection', 'Parts selection'),
      key: TAB_PARTS_SELECTION,
      component: selectedOperationId && <PartsSelection />,
      disabled: !hasData(selectedOperationSearchStatus),
    },
    {
      title: t('catalog.easy_basket.tab.add_labor_time', 'Add labor rate'),
      key: TAB_LABOR_RATE_SELECTION,
      component: <LaborTimesSelection />,
      disabled: !hasData(selectedOperationSearchStatus),
    },
  ];

  const renderTotalStrip = () => {
    switch (activeTabKey) {
      case TAB_PARTS_SELECTION:
        return <ReferencesTotalStrip onClickNext={handleClickOnNext} />;
      case TAB_LABOR_RATE_SELECTION:
        return <LaborTimesTotalStrip onClickNext={handleAddOperationToEstimate} />;
      default:
        return null;
    }
  };

  useEffect(() => {
    scrollToTopSmooth(offsetTop + OFFSET_SUB_HEADER + OFFSET_PADDING);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabKey]);

  return (
    <>
      <WideTabs
        tabData={tabData}
        activeKey={activeTabKey}
        variant={'greyBlue'}
        onChange={(tab) => setActiveTabKey(tab as EasyBasketTabs)}
      />
      {renderTotalStrip()}
    </>
  );
};

export default OperationsSection;
